// GameSettingsTile — solid-gradient per-game card.
//
// Single source of truth for "the colorful tile that represents a
// game in admin surfaces". Used in two places:
//
//   • / (admin dashboard "Games" row) — passes `matches` + `earnings`
//     to surface live stats inside the tile alongside the navigation
//     affordance.
//   • /settings/games (Games hub index) — no stats; tile is purely
//     navigational ("tag · Configure" sub-line).
//
// Gradient comes from theme.gradient on the GameModule, so the card
// visually matches what the player sees on their lobby tile.
//
// 2026-05-17 merge: previously the dashboard had a separate "Game
// settings" row at the bottom rendering this tile as a pure shortcut,
// and a SEPARATE "Games" row in the middle with stat cards. They
// represented the same 5 games — duplicated vertical space. Merged
// into one row at the mid-dashboard slot: colourful + stat-bearing +
// tappable into per-game config.
//
// 2026-05-17 (height pass): Cards bumped taller — game logo is
// bigger, and the match-count + ₹ earnings now stack onto two rows
// instead of fighting for horizontal space on one cramped line.
// Mobile in particular reads dramatically better with the two-line
// stack since phone-width tiles only get ~150px of horizontal room.

import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import type { GAMES } from '@baaligames/shared-ui/games/registry';

type GameMeta = (typeof GAMES)[number];

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function GameSettingsTile({ game, matches, earnings }: {
  game: GameMeta;
  /** Optional — when provided, the tile shows "X matches · ₹Y" as
   *  the sub-line instead of the static "tag · Configure" label. */
  matches?: number;
  /** Optional — paired with `matches`. Platform fee earnings in
   *  the current range. Coloured green to read as platform revenue. */
  earnings?: number;
}) {
  // theme.gradient is a complete tailwind gradient string like
  // 'from-purple-600 to-fuchsia-800' or 'from-green-600 via-green-800
  // to-green-950'. Used verbatim — same as the player-end lobby tile.
  const gradient = game.theme?.gradient ?? 'from-slate-700 to-slate-900';
  const hasStats = matches !== undefined;

  // 2026-05-16 (Routing Restructure): GamesHub moved from `/games/*`
  // to `/settings/games/*`. Link targets the new canonical URL so
  // clicks don't go through the redirect hop.
  return (
    <Link
      to={`/settings/games/${game.id}`}
      className={`group relative block rounded-2xl overflow-hidden
                  bg-gradient-to-br ${gradient}
                  shadow-[0_1px_3px_rgba(0,0,0,0.06)] dark:shadow-none
                  ring-1 ring-inset ring-black/5 dark:ring-white/10
                  hover:brightness-110 active:scale-[0.99] transition`}
    >
      <div className="p-3.5 sm:p-4 flex items-center gap-3 sm:gap-3.5 text-white">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-white/15 ring-1 ring-white/20 grid place-items-center text-3xl shrink-0 backdrop-blur-sm">
          {game.theme?.logo
            ? <img src={game.theme.logo} alt="" className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
            : <span aria-hidden>{game.theme?.emoji ?? '🎮'}</span>}
        </div>

        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight truncate text-white text-[13.5px] sm:text-[15px]">
            {game.displayName}
          </div>
          {hasStats ? (
            // Stats variant — dashboard usage. Stacked on two rows so
            // match count and platform earnings each get their own
            // line and stay readable even at narrow phone widths
            // (where the tile is ~150px wide and one-line stats wrap
            // or get clipped). Earnings tinted light-green for
            // revenue-at-a-glance without breaking gradient harmony.
            <div className="mt-1 tabular-nums">
              <div className="text-[11.5px] sm:text-[12.5px] font-medium text-white/85 truncate leading-tight">
                <span className="font-bold">{(matches ?? 0).toLocaleString('en-IN')}</span>
                <span className="opacity-75"> match{(matches ?? 0) === 1 ? '' : 'es'}</span>
              </div>
              <div className="text-[12.5px] sm:text-[13.5px] font-bold text-emerald-200 truncate leading-tight mt-0.5">
                {inr(earnings ?? 0)}
              </div>
            </div>
          ) : (
            // Plain variant — Games hub usage. Just a Configure hint.
            <div className="text-[10.5px] sm:text-[11.5px] font-medium text-white/70 truncate mt-1">
              {game.tag ?? 'Game'} · Configure
            </div>
          )}
        </div>

        {/* Trailing icon — visible from sm+. Wrapped in a span because
            the Icon's own `material-symbols-rounded` class declares
            `display:inline-block` which can beat Tailwind's `hidden`
            in the cascade. The span has no competing display rule. */}
        <span className="hidden sm:inline-flex shrink-0">
          <Icon
            name={game.adminIcon ?? 'chevron_right'}
            size={22}
            className="text-white/80 group-hover:text-white"
          />
        </span>
      </div>
    </Link>
  );
}
